import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { SignInFacade } from '../../../application/sign-in-facade';
import { CenteredContainerComponent } from '../../../../../shared/ui/components/layout/centered-container/centered-container.component';
import { CardComponent } from '../../../../../shared/ui/components/card/card.component';
import { TenantLogoComponent } from '../../../../../shared/ui/components/tenant-logo/tenant-logo.component';
import { SignInFormComponent } from '../../components/sign-in-form/sign-in-form.component';
import { ErrorMessageComponent } from '../../../../../shared/ui/components/error-message/error-message.component';
import { LoadingSpinnerComponent } from '../../../../../shared/ui/components/loading-spinner/loading-spinner.component';
import { LegalFooterComponent } from '../../../../../shared/ui/components/legal-footer/legal-footer.component';
import { LoginCredentials } from '@core/auth';

/**
 * Sign-In Page
 *
 * Landing page for user authentication.
 * Displays tenant branding and sign-in form.
 */
@Component({
  selector: 'app-sign-in-page',
  standalone: true,
  imports: [
    CommonModule,
    CenteredContainerComponent,
    CardComponent,
    TenantLogoComponent,
    SignInFormComponent,
    ErrorMessageComponent,
    LoadingSpinnerComponent,
    LegalFooterComponent,
  ],
  providers: [SignInFacade], // Feature-level facade
  template: `
    @let vm = this.vm();
    <app-centered-container [maxWidth]="780" [fullHeight]="true">
      @if (vm.tenantLoading) {
        <app-loading-spinner
          message="Loading your organization..."
        ></app-loading-spinner>
      } @else {
        <app-card>
          <div class="brand-row">
            <div class="brand-lockup">
              <app-tenant-logo
                logoUrl="/assets/images/ross-robotics-logo.svg"
                tenantName=""
                [align]="'start'"
                altText="Company Logo"
              ></app-tenant-logo>
              <div class="brand-copy">
                <p class="brand-title">AIDA</p>
                <p class="brand-subtitle">by Ross Robotics</p>
              </div>
            </div>
          </div>

          <div class="sign-in-content">
            <div class="title-column">
              <h1 class="sign-in-title">Sign in</h1>
            </div>
            <div class="form-column">
              <app-error-message [message]="vm.errorMessage"></app-error-message>
              <app-sign-in-form
                [isSubmitting]="vm.isSubmitting"
                (formSubmit)="onFormSubmit($event)"
                (emailChange)="onEmailChange()"
              ></app-sign-in-form>
            </div>
          </div>

          <app-legal-footer
            class="page-legal-footer"
            companyName="Ross Robotics"
            [year]="vm.year"
          ></app-legal-footer>
        </app-card>
      }
    </app-centered-container>
  `,
  styles: [
    `
      .brand-row {
        margin-bottom: var(--aida-ref-spacing-600);
      }

      .brand-lockup {
        display: flex;
        align-items: center;
        gap: var(--aida-ref-spacing-200);
      }

      .brand-copy {
        display: flex;
        flex-direction: column;
        gap: var(--aida-ref-spacing-50);
      }

      .brand-title {
        margin: 0;
        font-size: var(--aida-ref-font-size-700);
        font-weight: var(--aida-ref-font-weight-bold);
        color: var(--color-brand-text);
        line-height: 1;
        font-family: var(--aida-ref-font-family-text);
      }

      .brand-subtitle {
        margin: 0;
        font-size: var(--aida-ref-font-size-600);
        font-weight: var(--aida-ref-font-weight-regular);
        color: var(--aida-colour-text-primary);
        line-height: 1.2;
        font-family: var(--aida-ref-font-family-text);
      }

      .sign-in-content {
        display: grid;
        grid-template-columns: minmax(100px, 180px) minmax(320px, 1fr);
        align-items: start;
        column-gap: var(--aida-ref-spacing-200);
      }

      .title-column {
        padding-top: var(--aida-ref-spacing-100);
      }

      .form-column {
        min-width: 0;
      }

      .sign-in-title {
        font-size: var(--aida-ref-font-size-700);
        font-weight: var(--aida-ref-font-weight-bold);
        color: var(--color-brand-text);
        margin: 0;
      }

      .page-legal-footer {
        margin-top: var(--aida-ref-spacing-300);
      }

      @media (max-width: 600px) {
        .brand-row {
          margin-bottom: var(--aida-ref-spacing-400);
        }

        .brand-title {
          font-size: var(--aida-ref-font-size-700);
        }

        .brand-subtitle {
          font-size: var(--aida-ref-font-size-500);
        }

        .sign-in-content {
          grid-template-columns: 1fr;
          row-gap: var(--aida-ref-spacing-300);
        }

        .title-column {
          padding-top: 0;
        }

        .sign-in-title {
          font-size: var(--aida-ref-font-size-800);
        }
      }
    `,
  ],
})
export class SignInPageComponent {
  // View model from facade - single source of truth for presentation
  readonly vm = this.signInFacade.vm;

  constructor(
    private readonly signInFacade: SignInFacade,
    private readonly route: ActivatedRoute
  ) {}

  onFormSubmit(credentials: LoginCredentials): void {
    // snapshot access is intentional: the sign-in page is a leaf route and is
    // never reused while active, so the returnUrl param is stable for the
    // lifetime of the component instance.
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    void this.signInFacade.submitAuthentication(credentials, returnUrl);
  }

  onEmailChange(): void {
    this.signInFacade.clearError();
  }
}
